import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { api } from '../../api/client';
import { errorMessageKey, unwrap } from '../../api/api-error';
import { ensureOk, ME_KEY, type Profile, useAuthOptions } from '../../auth/session';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Notice } from '../../components/ui/notice';
import { TextField } from '../../components/ui/text-field';
import { useI18n } from '../../i18n/i18n';

type Values = { password: string; code: string };

export function GoogleSection({ profile }: { profile: Profile }) {
  const { t } = useI18n();
  const options = useAuthOptions();
  const queryClient = useQueryClient();
  const schema = useMemo(
    () =>
      z.object({
        password: profile.hasPassword ? z.string().min(1, 'validation.required') : z.string(),
        code: profile.twoFactorEnabled ? z.string().trim().min(6, 'validation.code') : z.string(),
      }),
    [profile.hasPassword, profile.twoFactorEnabled],
  );
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', code: '' },
  });
  // Привязка и отвязка — после пароля и кода (ADR-0013).
  const proof = (values: Values) => ({
    ...(profile.hasPassword ? { password: values.password } : {}),
    ...(profile.twoFactorEnabled ? { code: values.code } : {}),
  });
  const unlink = useMutation({
    mutationFn: async (values: Values) =>
      ensureOk(await api.POST('/api/v1/me/google/unlink', { body: proof(values) })),
    onSuccess: async () => {
      form.reset();
      await queryClient.invalidateQueries({ queryKey: ME_KEY });
    },
  });
  // Сервер проверяет пароль и код и отдаёт адрес Google, дальше — обычный переход браузера.
  const link = useMutation({
    mutationFn: async (values: Values) =>
      unwrap(await api.POST('/api/v1/me/google/link', { body: proof(values) })),
    onSuccess: (result) => window.location.assign(result.url),
  });
  if (options.data?.googleEnabled !== true && !profile.googleLinked) return null;

  return (
    <Card title={t('profile.google.title')}>
      <p className="text-sm">
        {profile.googleLinked ? t('profile.google.isLinked') : t('profile.google.notLinked')}
      </p>
      <form
        className="space-y-3"
        onSubmit={form.handleSubmit((values) =>
          profile.googleLinked ? unlink.mutate(values) : link.mutate(values),
        )}
        noValidate
      >
        {profile.hasPassword && (
          <TextField
            control={form.control}
            name="password"
            type="password"
            label={t('auth.field.currentPassword')}
            autoComplete="current-password"
          />
        )}
        {profile.twoFactorEnabled && (
          <TextField
            control={form.control}
            name="code"
            label={t('auth.field.code')}
            autoComplete="one-time-code"
          />
        )}
        {link.isError && <Notice tone="danger">{t(errorMessageKey(link.error))}</Notice>}
        {unlink.isError && <Notice tone="danger">{t(errorMessageKey(unlink.error))}</Notice>}
        <Button
          type="submit"
          variant={profile.googleLinked ? 'danger' : 'secondary'}
          disabled={link.isPending || unlink.isPending}
        >
          {profile.googleLinked ? t('profile.google.unlink') : t('profile.google.link')}
        </Button>
      </form>
    </Card>
  );
}
